// DrawingCanvas.tsx - Fixed version with proper transform capabilities
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTool, TOOL_MODES } from '../../context/ToolContext';
import { useProject } from '../../context/ProjectContext';

interface DrawingPoint {
  x: number;
  y: number;
  timestamp: number;
}

interface DrawingPath {
  id: string;
  type: 'stroke' | 'shape';
  points?: DrawingPoint[];
  startPoint?: DrawingPoint;
  endPoint?: DrawingPoint;
  color: string;
  size: number;
  brushType?: string;
  tool: string;
}

const DrawingCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState<DrawingPoint[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [draggedElement, setDraggedElement] = useState<string | null>(null);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [originalBounds, setOriginalBounds] = useState<any>(null);
  const [elementCenter, setElementCenter] = useState({ x: 0, y: 0 });
  
  // Get contexts
  const toolContext = useTool();
  const { 
    currentMode, 
    selectedColor, 
    brushSize, 
    eraserSize,
    addToUndoStack,
    selectElement,
    clearSelection,
    selectedElement
  } = toolContext;
  
  const brushType = (toolContext as any).brushType || 'smooth';
  
  const projectContext = useProject();
  const { currentPageIndex, updatePage } = projectContext;
  const currentPage = projectContext.pages[currentPageIndex];
  
  const paths: DrawingPath[] = currentPage && currentPage.drawings ? currentPage.drawings as DrawingPath[] : [];

  console.log('🎨 DrawingCanvas - Mode:', currentMode, 'Paths:', paths.length, 'Selected:', selectedElement);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
    
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    ctxRef.current = ctx;
    redrawCanvas();
  }, []);

  // Get bounds of any drawing element
  const getElementBounds = (path: DrawingPath) => {
    if (path.type === 'stroke' && path.points && path.points.length > 0) {
      const xs = path.points.map(p => p.x);
      const ys = path.points.map(p => p.y);
      return {
        minX: Math.min(...xs),
        minY: Math.min(...ys),
        maxX: Math.max(...xs),
        maxY: Math.max(...ys),
        width: Math.max(...xs) - Math.min(...xs),
        height: Math.max(...ys) - Math.min(...ys)
      };
    } else if (path.type === 'shape' && path.startPoint && path.endPoint) {
      return {
        minX: Math.min(path.startPoint.x, path.endPoint.x),
        minY: Math.min(path.startPoint.y, path.endPoint.y),
        maxX: Math.max(path.startPoint.x, path.endPoint.x),
        maxY: Math.max(path.startPoint.y, path.endPoint.y),
        width: Math.abs(path.endPoint.x - path.startPoint.x),
        height: Math.abs(path.endPoint.y - path.startPoint.y)
      };
    }
    return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
  };

  // Get transform handles - made bigger and more kid-friendly
  const getTransformHandles = (bounds: any) => {
    const centerX = bounds.minX + bounds.width / 2;
    const centerY = bounds.minY + bounds.height / 2;
    
    return {
      'nw': { x: bounds.minX, y: bounds.minY, cursor: 'nw-resize' },
      'ne': { x: bounds.maxX, y: bounds.minY, cursor: 'ne-resize' },
      'se': { x: bounds.maxX, y: bounds.maxY, cursor: 'se-resize' },
      'sw': { x: bounds.minX, y: bounds.maxY, cursor: 'sw-resize' },
      'rotate': { x: centerX, y: bounds.minY - 40, cursor: 'grab' }, // Further away for easier access
      'delete': { x: bounds.maxX + 25, y: bounds.minY - 15, cursor: 'pointer' } // Further away
    };
  };

  // Check which handle is clicked
  const getClickedHandle = (point: DrawingPoint, bounds: any) => {
    const handles = getTransformHandles(bounds);
    
    for (const [handleName, handle] of Object.entries(handles)) {
      const distance = Math.sqrt((point.x - handle.x) ** 2 + (point.y - handle.y) ** 2);
      if (handleName === 'delete' || handleName === 'rotate') {
        if (distance <= 15) return handleName;
      } else {
        if (distance <= 8) return handleName;
      }
    }
    
    return null;
  };

  // Rotate a point around a center
  const rotatePoint = (point: DrawingPoint, center: { x: number; y: number }, angle: number): DrawingPoint => {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const dx = point.x - center.x;
    const dy = point.y - center.y;
    
    return {
      x: center.x + (dx * cos - dy * sin),
      y: center.y + (dx * sin + dy * cos),
      timestamp: point.timestamp
    };
  };

  // Fixed drawing function with proper eraser support
const drawPath = (path: DrawingPath, ctx: CanvasRenderingContext2D) => {
    // Save the current composite operation
    const originalCompositeOperation = ctx.globalCompositeOperation;
    
    if (path.type === 'stroke' && path.points && path.points.length > 1) {
      // Set composite operation for eraser
      if (path.tool === TOOL_MODES.ERASER) {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.strokeStyle = 'rgba(0,0,0,1)'; // Color doesn't matter for erasing
      } else {
        ctx.globalCompositeOperation = 'source-over';
        ctx.strokeStyle = path.color;
      }
      
      ctx.lineWidth = path.size;
      ctx.beginPath();
      ctx.moveTo(path.points[0].x, path.points[0].y);
      for (let i = 1; i < path.points.length; i++) {
        ctx.lineTo(path.points[i].x, path.points[i].y);
      }
      ctx.stroke();
    } else if (path.type === 'shape' && path.startPoint && path.endPoint) {
      // Always use normal drawing for shapes (shapes can't be erasers)
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = path.color;
      ctx.lineWidth = path.size;
      ctx.beginPath();
      
      const width = path.endPoint.x - path.startPoint.x;
      const height = path.endPoint.y - path.startPoint.y;
      
      if (path.tool === TOOL_MODES.CIRCLE) {
        const radius = Math.sqrt(width * width + height * height) / 2;
        const centerX = path.startPoint.x + width / 2;
        const centerY = path.startPoint.y + height / 2;
        ctx.arc(centerX, centerY, Math.abs(radius), 0, Math.PI * 2);
      } else if (path.tool === TOOL_MODES.RECTANGLE) {
        ctx.rect(path.startPoint.x, path.startPoint.y, width, height);
      } else if (path.tool === TOOL_MODES.LINE) {
        ctx.moveTo(path.startPoint.x, path.startPoint.y);
        ctx.lineTo(path.endPoint.x, path.endPoint.y);
      }
      ctx.stroke();
    }
    
    // Always restore the original composite operation
    ctx.globalCompositeOperation = originalCompositeOperation;
  };

  // Redraw everything
  const redrawCanvas = useCallback(() => {
    const ctx = ctxRef.current;
    const canvas = canvasRef.current;
    if (!ctx || !canvas) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw all paths
    paths.forEach((path: DrawingPath) => {
      drawPath(path, ctx);
    });

    // Show selection with transform handles
    if (selectedElement && currentMode === TOOL_MODES.SELECT) {
      const selectedPath = paths.find(p => p.id === selectedElement);
      if (selectedPath) {
        const bounds = getElementBounds(selectedPath);
        const handles = getTransformHandles(bounds);
        
        // Draw selection outline
        ctx.strokeStyle = '#0066ff';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(bounds.minX - 5, bounds.minY - 5, bounds.width + 10, bounds.height + 10);
        ctx.setLineDash([]);
        
        // Draw transform handles - bigger and more kid-friendly
        Object.entries(handles).forEach(([handleName, handle]) => {
          if (handleName === 'delete') {
            // Delete button - bigger red square with X
            ctx.fillStyle = '#ff4444';
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 3;
            ctx.fillRect(handle.x - 15, handle.y - 15, 30, 30);
            ctx.strokeRect(handle.x - 15, handle.y - 15, 30, 30);
            
            // Draw bigger X
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 4;
            ctx.beginPath();
            ctx.moveTo(handle.x - 8, handle.y - 8);
            ctx.lineTo(handle.x + 8, handle.y + 8);
            ctx.moveTo(handle.x + 8, handle.y - 8);
            ctx.lineTo(handle.x - 8, handle.y + 8);
            ctx.stroke();
          } else if (handleName === 'rotate') {
            // Rotation handle - bigger green circle with clearer arrow
            ctx.fillStyle = '#22c55e';
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.arc(handle.x, handle.y, 12, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
            
            // Draw bigger, clearer rotation arrow
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.arc(handle.x, handle.y, 8, -Math.PI/2, Math.PI);
            ctx.stroke();
            
            // Bigger arrow head
            ctx.beginPath();
            ctx.moveTo(handle.x - 6, handle.y + 6);
            ctx.lineTo(handle.x - 3, handle.y + 9);
            ctx.lineTo(handle.x - 9, handle.y + 3);
            ctx.closePath();
            ctx.fill();
            
            // Connection line to element
            ctx.setLineDash([3, 3]);
            ctx.strokeStyle = '#22c55e';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(handle.x, handle.y + 12);
            ctx.lineTo(bounds.minX + bounds.width / 2, bounds.minY);
            ctx.stroke();
            ctx.setLineDash([]);
          } else {
            // Resize handles - bigger blue squares
            ctx.fillStyle = '#0066ff';
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 3;
            ctx.fillRect(handle.x - 8, handle.y - 8, 16, 16);
            ctx.strokeRect(handle.x - 8, handle.y - 8, 16, 16);
            
            // Add resize arrows to make it clearer
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 2;
            ctx.beginPath();
            
            // Draw arrows based on corner
            if (handleName === 'nw' || handleName === 'se') {
              // Diagonal arrows
              ctx.moveTo(handle.x - 4, handle.y - 2);
              ctx.lineTo(handle.x - 2, handle.y - 4);
              ctx.moveTo(handle.x + 2, handle.y + 4);
              ctx.lineTo(handle.x + 4, handle.y + 2);
            } else {
              // Other diagonal
              ctx.moveTo(handle.x - 4, handle.y + 2);
              ctx.lineTo(handle.x - 2, handle.y + 4);
              ctx.moveTo(handle.x + 2, handle.y - 4);
              ctx.lineTo(handle.x + 4, handle.y - 2);
            }
            ctx.stroke();
          }
        });
      }
    }
  }, [paths, selectedElement, currentMode]);

  useEffect(() => {
    redrawCanvas();
  }, [redrawCanvas]);

  // Get mouse point
  const getPoint = (e: React.MouseEvent): DrawingPoint => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      timestamp: Date.now()
    };
  };

  // Handle mouse down
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const point = getPoint(e);
    
    console.log('🖱️ Mouse down at:', point, 'Mode:', currentMode);
    
    // SELECT MODE
    if (currentMode === TOOL_MODES.SELECT) {
      if (selectedElement) {
        const selectedPath = paths.find(p => p.id === selectedElement);
        if (selectedPath) {
          const bounds = getElementBounds(selectedPath);
          const clickedHandle = getClickedHandle(point, bounds);
          
          if (clickedHandle) {
            console.log('🎯 Clicked handle:', clickedHandle);
            
            if (clickedHandle === 'delete') {
              // Delete the element
              console.log('🗑️ Deleting element:', selectedElement);
              const updatedPaths = paths.filter(p => p.id !== selectedElement);
              updatePage(currentPageIndex, { drawings: updatedPaths });
              addToUndoStack({
                type: 'DELETE_DRAWING',
                pageIndex: currentPageIndex,
                drawing: selectedPath
              });
              clearSelection();
              return;
            } else if (clickedHandle === 'rotate') {
              // Start rotation
              console.log('🔄 Starting rotation');
              setIsRotating(true);
              setDraggedElement(selectedElement);
              setDragStart(point);
              setElementCenter({
                x: bounds.minX + bounds.width / 2,
                y: bounds.minY + bounds.height / 2
              });
              return;
            } else {
              // Start resizing
              console.log('🔧 Starting resize with handle:', clickedHandle);
              setIsResizing(true);
              setResizeHandle(clickedHandle);
              setDraggedElement(selectedElement);
              setDragStart(point);
              setOriginalBounds(bounds);
              return;
            }
          }
          
          // Check if clicking on element to drag (not on handles)
          if (point.x >= bounds.minX - 10 && point.x <= bounds.maxX + 10 &&
              point.y >= bounds.minY - 10 && point.y <= bounds.maxY + 10) {
            console.log('🖱️ Starting drag of:', selectedElement);
            setIsDragging(true);
            setDraggedElement(selectedElement);
            setDragStart(point);
            return;
          }
        }
      }
      return; // Selection will be handled by click event
    }
    
    // DRAWING MODES
    const drawingModes = [TOOL_MODES.DRAW, TOOL_MODES.ERASER, TOOL_MODES.CIRCLE, TOOL_MODES.RECTANGLE, TOOL_MODES.LINE];
    if (drawingModes.includes(currentMode as any)) {
      console.log('🖌️ Starting to draw with:', currentMode);
      setIsDrawing(true);
      setCurrentPath([point]);
    }
  };

  // Handle mouse move
  const handleMouseMove = (e: React.MouseEvent) => {
    const point = getPoint(e);
    
    // Handle rotation
    if (isRotating && draggedElement && elementCenter) {
      const currentAngle = Math.atan2(point.y - elementCenter.y, point.x - elementCenter.x);
      const startAngle = Math.atan2(dragStart.y - elementCenter.y, dragStart.x - elementCenter.x);
      const deltaAngle = currentAngle - startAngle;
      
      console.log('🔄 Rotating, angle:', (deltaAngle * 180 / Math.PI).toFixed(1), 'degrees');
      
      const updatedPaths = paths.map((path: DrawingPath) => {
        if (path.id === draggedElement) {
          if (path.type === 'stroke' && path.points) {
            return {
              ...path,
              points: path.points.map(p => rotatePoint(p, elementCenter, deltaAngle))
            };
          } else if (path.type === 'shape' && path.startPoint && path.endPoint) {
            return {
              ...path,
              startPoint: rotatePoint(path.startPoint, elementCenter, deltaAngle),
              endPoint: rotatePoint(path.endPoint, elementCenter, deltaAngle)
            };
          }
        }
        return path;
      });
      
      updatePage(currentPageIndex, { drawings: updatedPaths });
      setDragStart(point);
      return;
    }
    
    // Handle resizing - FIXED to work properly
    if (isResizing && draggedElement && resizeHandle && originalBounds) {
      const dx = point.x - dragStart.x;
      const dy = point.y - dragStart.y;
      
      console.log('🔧 Resizing with handle:', resizeHandle, 'delta:', { dx, dy });
      
      const updatedPaths = paths.map((path: DrawingPath) => {
        if (path.id === draggedElement) {
          if (path.type === 'shape' && path.startPoint && path.endPoint) {
            const newPath = { ...path };
            
            // Calculate new bounds based on resize handle
            let newStartX = originalBounds.minX;
            let newStartY = originalBounds.minY;
            let newEndX = originalBounds.maxX;
            let newEndY = originalBounds.maxY;
            
            switch (resizeHandle) {
              case 'nw': // Top-left corner
                newStartX = originalBounds.minX + dx;
                newStartY = originalBounds.minY + dy;
                break;
              case 'ne': // Top-right corner
                newEndX = originalBounds.maxX + dx;
                newStartY = originalBounds.minY + dy;
                break;
              case 'se': // Bottom-right corner
                newEndX = originalBounds.maxX + dx;
                newEndY = originalBounds.maxY + dy;
                break;
              case 'sw': // Bottom-left corner
                newStartX = originalBounds.minX + dx;
                newEndY = originalBounds.maxY + dy;
                break;
            }
            
            // Ensure minimum size
            const minSize = 20;
            if (Math.abs(newEndX - newStartX) < minSize) {
              newEndX = newStartX + (newEndX > newStartX ? minSize : -minSize);
            }
            if (Math.abs(newEndY - newStartY) < minSize) {
              newEndY = newStartY + (newEndY > newStartY ? minSize : -minSize);
            }
            
            newPath.startPoint = { ...newPath.startPoint, x: newStartX, y: newStartY, timestamp: Date.now() };
            newPath.endPoint = { ...newPath.endPoint, x: newEndX, y: newEndY, timestamp: Date.now() };
            
            return newPath;
          } else if (path.type === 'stroke' && path.points) {
            // For strokes, scale all points proportionally
            const scaleX = resizeHandle.includes('e') ? 
              (originalBounds.width + dx) / originalBounds.width : 
              resizeHandle.includes('w') ? 
              (originalBounds.width - dx) / originalBounds.width : 1;
            
            const scaleY = resizeHandle.includes('s') ? 
              (originalBounds.height + dy) / originalBounds.height : 
              resizeHandle.includes('n') ? 
              (originalBounds.height - dy) / originalBounds.height : 1;
            
            const centerX = originalBounds.minX + originalBounds.width / 2;
            const centerY = originalBounds.minY + originalBounds.height / 2;
            
            return {
              ...path,
              points: path.points.map(p => ({
                ...p,
                x: centerX + (p.x - centerX) * Math.max(scaleX, 0.1),
                y: centerY + (p.y - centerY) * Math.max(scaleY, 0.1)
              }))
            };
          }
        }
        return path;
      });
      
      updatePage(currentPageIndex, { drawings: updatedPaths });
      return;
    }
    
    // Handle dragging
    if (isDragging && draggedElement) {
      const dx = point.x - dragStart.x;
      const dy = point.y - dragStart.y;
      
      console.log('🖱️ Dragging, delta:', { dx, dy });
      
      const updatedPaths = paths.map((path: DrawingPath) => {
        if (path.id === draggedElement) {
          if (path.type === 'stroke' && path.points) {
            return {
              ...path,
              points: path.points.map(p => ({
                ...p,
                x: p.x + dx,
                y: p.y + dy
              }))
            };
          } else if (path.type === 'shape' && path.startPoint && path.endPoint) {
            return {
              ...path,
              startPoint: { ...path.startPoint, x: path.startPoint.x + dx, y: path.startPoint.y + dy, timestamp: Date.now() },
              endPoint: { ...path.endPoint, x: path.endPoint.x + dx, y: path.endPoint.y + dy, timestamp: Date.now() }
            };
          }
        }
        return path;
      });
      
      updatePage(currentPageIndex, { drawings: updatedPaths });
      setDragStart(point);
      return;
    }
    
    // Handle drawing
    if (isDrawing) {
      const newPath = [...currentPath, point];
      setCurrentPath(newPath);
      
      const ctx = ctxRef.current;
      if (!ctx) return;
      
      // Redraw everything + current path
      redrawCanvas();
      
      if (currentMode === TOOL_MODES.DRAW || currentMode === TOOL_MODES.ERASER) {
        ctx.strokeStyle = selectedColor;
        ctx.lineWidth = currentMode === TOOL_MODES.ERASER ? eraserSize : brushSize;
        ctx.beginPath();
        ctx.moveTo(newPath[0].x, newPath[0].y);
        for (let i = 1; i < newPath.length; i++) {
          ctx.lineTo(newPath[i].x, newPath[i].y);
        }
        ctx.stroke();
      } else {
        // Shape preview
        const start = currentPath[0];
        ctx.strokeStyle = selectedColor;
        ctx.lineWidth = brushSize;
        ctx.beginPath();
        
        const width = point.x - start.x;
        const height = point.y - start.y;
        
        if (currentMode === TOOL_MODES.CIRCLE) {
          const radius = Math.sqrt(width * width + height * height) / 2;
          const centerX = start.x + width / 2;
          const centerY = start.y + height / 2;
          ctx.arc(centerX, centerY, Math.abs(radius), 0, Math.PI * 2);
        } else if (currentMode === TOOL_MODES.RECTANGLE) {
          ctx.rect(start.x, start.y, width, height);
        } else if (currentMode === TOOL_MODES.LINE) {
          ctx.moveTo(start.x, start.y);
          ctx.lineTo(point.x, point.y);
        }
        ctx.stroke();
      }
    }
  };

  // Handle mouse up
  const handleMouseUp = () => {
    console.log('🖱️ Mouse up - Dragging:', isDragging, 'Resizing:', isResizing, 'Rotating:', isRotating, 'Drawing:', isDrawing);
    
    if (isRotating) {
      console.log('✅ Finished rotating');
      setIsRotating(false);
      setDraggedElement(null);
      setElementCenter({ x: 0, y: 0 });
      return;
    }
    
    if (isResizing) {
      console.log('✅ Finished resizing');
      setIsResizing(false);
      setResizeHandle(null);
      setDraggedElement(null);
      setOriginalBounds(null);
      return;
    }
    
    if (isDragging) {
      console.log('✅ Finished dragging');
      setIsDragging(false);
      setDraggedElement(null);
      return;
    }
    
    if (isDrawing && currentPath.length > 1) {
      console.log('✅ Saving drawing with', currentPath.length, 'points');
      
      const newPath: DrawingPath = {
        id: Date.now().toString(),
        type: (currentMode === TOOL_MODES.DRAW || currentMode === TOOL_MODES.ERASER) ? 'stroke' : 'shape',
        color: selectedColor,
        size: currentMode === TOOL_MODES.ERASER ? eraserSize : brushSize,
        brushType: brushType,
        tool: currentMode
      };

      if (newPath.type === 'stroke') {
        newPath.points = currentPath;
      } else {
        newPath.startPoint = currentPath[0];
        newPath.endPoint = currentPath[currentPath.length - 1];
      }

      const updatedPaths = [...paths, newPath];
      updatePage(currentPageIndex, { drawings: updatedPaths });
      
      console.log('✅ Saved new path. Total paths now:', updatedPaths.length);
    }
    
    setIsDrawing(false);
    setCurrentPath([]);
  };

  // Handle click for selection
  const handleClick = (e: React.MouseEvent) => {
    if (currentMode !== TOOL_MODES.SELECT || isDragging || isResizing || isRotating) return;
    
    const point = getPoint(e);
    console.log('🎯 Click for selection at:', point);
    
    // Find clicked element
    let found = null;
    for (let i = paths.length - 1; i >= 0; i--) {
      const path = paths[i];
      
      if (path.type === 'stroke' && path.points) {
        for (const p of path.points) {
          const dist = Math.sqrt((point.x - p.x) ** 2 + (point.y - p.y) ** 2);
          if (dist <= Math.max(path.size / 2 + 5, 10)) {
            found = path;
            break;
          }
        }
      } else if (path.type === 'shape' && path.startPoint && path.endPoint) {
        const minX = Math.min(path.startPoint.x, path.endPoint.x) - 10;
        const maxX = Math.max(path.startPoint.x, path.endPoint.x) + 10;
        const minY = Math.min(path.startPoint.y, path.endPoint.y) - 10;
        const maxY = Math.max(path.startPoint.y, path.endPoint.y) + 10;
        
        if (point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY) {
          found = path;
        }
      }
      
      if (found) break;
    }
    
    if (found) {
      console.log('🎯 Selected:', found.id);
      selectElement(found.id, 'drawing');
    } else {
      console.log('🎯 Cleared selection');
      clearSelection();
    }
  };

  const isDrawingMode = [TOOL_MODES.DRAW, TOOL_MODES.ERASER, TOOL_MODES.CIRCLE, TOOL_MODES.RECTANGLE, TOOL_MODES.LINE].includes(currentMode as any);

  return (
    <div className="absolute inset-0">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleClick}
        style={{
          pointerEvents: (isDrawingMode || currentMode === TOOL_MODES.SELECT) ? 'auto' : 'none',
          cursor: isDragging ? 'grabbing' :
                  isRotating ? 'grabbing' :
                  isResizing && resizeHandle ? (
                    resizeHandle === 'nw' || resizeHandle === 'se' ? 'nw-resize' :
                    resizeHandle === 'ne' || resizeHandle === 'sw' ? 'ne-resize' : 'default'
                  ) :
                  currentMode === TOOL_MODES.SELECT ? 'pointer' :
                  isDrawingMode ? 'crosshair' : 'default',
          touchAction: 'none'
        }}
      />
    </div>
  );
};

export default DrawingCanvas;

